import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ShieldCheck, ArrowLeft } from 'lucide-react';
import { api } from '../../../services/api';
import { SignatureManager } from '../../../components/signatures/SignatureManager';

type SignatureResponse = {
  signature_url: string | null;
  updated_at: string | null;
  has_signature: boolean;
};

type SignatureGateProps = {
  siteName: string;
  /** Online flag from useOffline — drives fail-open behaviour. */
  isOnline: boolean;
  /** Inspector has (or already had) a signature on file; enter the inspection. */
  onContinue: () => void;
  /** Bail out of the inspection entirely. */
  onBack: () => void;
};

/**
 * One-time signature setup gate. An inspector with NO signature on file is
 * required to create one before their first inspection (Jim, 2026-06-29: it
 * wasn't making them sign). Once a signature is saved it's reused on every
 * inspection — the gate auto-advances and never interrupts them again
 * (Chris, 2026-06-30: "one time only ... not an every time ran thing").
 *
 * The submit flow freezes the saved signature onto each inspection
 * (/use-saved-signature) so reports stay immutable snapshots, and the report
 * generator already falls back to the inspector's saved signature.
 *
 * Fail-open offline / on a failed check: we can't confirm whether a signature
 * exists, so we don't block — a field inspector with no signal is never
 * stranded, and a report can't be generated offline anyway.
 */
export function SignatureGate({
  siteName,
  isOnline,
  onContinue,
  onBack,
}: SignatureGateProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['signature', 'user', undefined],
    queryFn: async () => {
      const res = await api.get<SignatureResponse>('/users/me/signature');
      return res.data;
    },
    enabled: isOnline,
    retry: 1,
  });

  // The gate only stops a first-time inspector with no signature on file. A
  // signature already present (or an inability to check) advances straight
  // through to the inspection — no per-inspection confirmation step.
  const pass = !isOnline || isError || !!data?.has_signature;

  useEffect(() => {
    if (pass) onContinue();
  }, [pass, onContinue]);

  // While checking, or once we've decided to pass through (the parent unmounts
  // this a tick later), show a neutral loader rather than flashing the gate.
  if (isLoading || pass) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center p-8">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Preparing inspection…
          </p>
        </div>
      </div>
    );
  }

  // Online, resolved, and NO signature on file → require a one-time capture.
  // Saving invalidates the shared query (has_signature flips true) and also
  // fires onSaved, either of which advances into the inspection.
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">
              Add your signature
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {siteName}
            </p>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            Before your first inspection, add a signature. It&apos;s saved to
            your account and applied to every report automatically — you&apos;ll
            only do this once.
          </p>
          <SignatureManager mode="user" onSaved={onContinue} />
        </div>
        <button
          type="button"
          onClick={onBack}
          className="mt-6 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>
    </div>
  );
}

export default SignatureGate;
